/**
 * PDOK Locatieserver Service
 *
 * Free Dutch address lookup service using the government BAG database. Documentation:
 * https://api.pdok.nl/bzk/locatieserver/search/v3_1/
 */
import { logger } from "@/logger";

const PDOK_BASE_URL = "https://api.pdok.nl/bzk/locatieserver/search/v3_1";

export interface PDOKAddress {
  id: string;
  weergavenaam: string; // Display name
  straatnaam?: string; // Street name
  huisnummer?: number; // House number
  huisletter?: string; // House letter (e.g., "A")
  huisnummertoevoeging?: string; // House number addition (e.g., "bis")
  postcode?: string; // Postal code
  woonplaatsnaam?: string; // City name
  gemeentenaam?: string; // Municipality name
  provincienaam?: string; // Province name
  centroide_ll?: string; // Coordinates in "POINT(lon lat)" format
}

export interface PDOKSuggestResponse {
  response: {
    numFound: number;
    start: number;
    maxScore: number;
    docs: PDOKAddress[];
  };
}

export interface PDOKLookupResponse {
  response: {
    numFound: number;
    start: number;
    maxScore: number;
    docs: PDOKAddress[];
  };
}

export interface ParsedAddress {
  addressLine: string;
  zipCode: string;
  city: string;
  state: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

/** Parse coordinates from PDOK centroide_ll format Format: "POINT(longitude latitude)" */
function parseCoordinates(centroide_ll?: string):
  | {
      latitude: number;
      longitude: number;
    }
  | undefined {
  if (!centroide_ll) {
    return undefined;
  }

  const match = centroide_ll.match(/POINT\(([0-9.]+)\s+([0-9.]+)\)/);
  if (!match) {
    return undefined;
  }

  return {
    longitude: parseFloat(match[1]),
    latitude: parseFloat(match[2]),
  };
}

/** Build full street address from PDOK address components */
function buildAddressLine(address: PDOKAddress): string {
  const parts: string[] = [];

  if (address.straatnaam) {
    parts.push(address.straatnaam);
  }

  if (address.huisnummer) {
    let houseNumber = address.huisnummer.toString();
    if (address.huisletter) {
      houseNumber += address.huisletter;
    }
    if (address.huisnummertoevoeging) {
      houseNumber += `-${address.huisnummertoevoeging}`;
    }
    parts.push(houseNumber);
  }

  return parts.join(" ");
}

/** Parse PDOK address into our application's address format */
export function parsePDOKAddress(address: PDOKAddress): ParsedAddress {
  return {
    addressLine: buildAddressLine(address),
    zipCode: address.postcode || "",
    city: address.woonplaatsnaam || "",
    state: address.provincienaam || "",
    coordinates: parseCoordinates(address.centroide_ll),
  };
}

/**
 * Search for address suggestions based on user input Returns up to 10 suggestions
 *
 * @param query - User input (e.g., "Kalverstraat 1, Amsterdam")
 * @param limit - Maximum number of results (default: 10)
 */
export async function suggestAddresses(query: string, limit: number = 10): Promise<PDOKAddress[]> {
  if (!query || query.length < 2) {
    return [];
  }

  const params = new URLSearchParams({
    q: query,
    rows: limit.toString(),
    fq: "type:adres", // Only return addresses (not streets, cities, etc.)
  });

  try {
    const response = await fetch(`${PDOK_BASE_URL}/suggest?${params}`);

    if (!response.ok) {
      logger.error("PDOK API error", { status: response.status, statusText: response.statusText });
      return [];
    }

    const data = (await response.json()) as PDOKSuggestResponse;
    return data.response.docs;
  } catch (error) {
    logger.error("Failed to fetch address suggestions", { error });
    return [];
  }
}

/**
 * Lookup detailed information for a specific address by ID
 *
 * @param id - PDOK address ID from suggestion
 */
export async function lookupAddress(id: string): Promise<PDOKAddress | null> {
  const params = new URLSearchParams({
    id: id,
  });

  try {
    const response = await fetch(`${PDOK_BASE_URL}/lookup?${params}`);

    if (!response.ok) {
      logger.error("PDOK API error", { status: response.status, statusText: response.statusText });
      return null;
    }

    const data = (await response.json()) as PDOKLookupResponse;

    if (data.response.numFound === 0) {
      return null;
    }

    return data.response.docs[0];
  } catch (error) {
    logger.error("Failed to lookup address", { error });
    return null;
  }
}

/** Search for addresses and return parsed results Convenience method that combines suggest + parse */
export async function searchAddresses(query: string, limit: number = 10): Promise<ParsedAddress[]> {
  const addresses = await suggestAddresses(query, limit);
  return addresses.map(parsePDOKAddress);
}

/**
 * Get full address details by ID and return parsed result Convenience method that combines lookup +
 * parse
 */
export async function getAddressById(id: string): Promise<ParsedAddress | null> {
  const address = await lookupAddress(id);
  if (!address) {
    return null;
  }
  return parsePDOKAddress(address);
}
